import React, { useState, useEffect } from 'react';
import { slpService } from '../../lib/services/slp';
import CalendarView from './CalendarView';
import ActivityList from './ActivityList';
import ActivityForm from './ActivityForm';
import StudentRecords from './StudentRecords';
import StatisticsPanel from './StatisticsPanel';
import './SlpModule.css';

const SlpModule = ({ currentUser, onBack, onTabChange }) => {
  const [currentView, setCurrentView] = useState('calendar'); // 'calendar', 'activities', 'students'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activities, setActivities] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    category: 'all',
    grade: 'all',
    month: 'all'
  });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [activitiesData, studentsData, teachersData, statistics] = await Promise.all([
        slpService.getActivities(),
        slpService.getStudents(),
        slpService.getTeachers(),
        slpService.getSlpStats()
      ]);

      setActivities(activitiesData);
      setStudents(studentsData);
      setTeachers(teachersData);
      setStats(statistics);
    } catch (err) {
      console.error('Error loading SLP data:', err);
      setError('載入學生學習歷程資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowActivityForm(true);
  };

  const handleActivitySelect = (activity) => {
    setSelectedActivity(activity);
    setShowActivityForm(true);
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
  };

  const handleActivitySave = async (activityData) => {
    try {
      if (selectedActivity) {
        // Update existing activity
        const updatedActivity = await slpService.updateActivity(selectedActivity.id, activityData);
        setActivities(prev => prev.map(activity => 
          activity.id === selectedActivity.id ? updatedActivity : activity
        ));
      } else {
        // Add new activity
        const newActivity = await slpService.addActivity(activityData);
        setActivities(prev => [...prev, newActivity]);
      }
      
      setShowActivityForm(false);
      setSelectedActivity(null);
      
      // Reload statistics
      const updatedStats = await slpService.getSlpStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error saving activity:', err);
      setError('儲存活動時發生錯誤');
    }
  };

  const handleActivityDelete = async (activityId) => {
    try {
      await slpService.deleteActivity(activityId);
      setActivities(prev => prev.filter(activity => activity.id !== activityId));
      
      // Reload statistics
      const updatedStats = await slpService.getSlpStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error deleting activity:', err);
      setError('刪除活動時發生錯誤');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const getFilteredActivities = () => {
    let filtered = [...activities];

    // Filter by year
    if (filters.year !== 'all') {
      filtered = filtered.filter(activity => 
        activity.activity_date && activity.activity_date.startsWith(filters.year.toString())
      );
    }

    // Filter by category
    if (filters.category !== 'all') {
      filtered = filtered.filter(activity => activity.activity_category === filters.category);
    }

    // Filter by grade
    if (filters.grade !== 'all') {
      filtered = filtered.filter(activity => 
        activity.target_grades && activity.target_grades.includes(filters.grade)
      );
    }

    // Filter by month
    if (filters.month !== 'all') {
      const monthStr = filters.month.toString().padStart(2, '0');
      filtered = filtered.filter(activity => 
        activity.activity_date && activity.activity_date.includes(`-${monthStr}-`)
      );
    }

    return filtered;
  };

  if (loading) {
    return (
      <div className="slp-module">
        <div className="slp-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>學生學習歷程系統 (SLP)</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入學生學習歷程中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slp-module">
        <div className="slp-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>學生學習歷程系統 (SLP)</h1>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadInitialData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="slp-module">
      <div className="slp-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>學生學習歷程系統 (SLP)</h1>
        <div className="slp-nav">
          <button 
            className={`nav-btn ${currentView === 'calendar' ? 'active' : ''}`}
            onClick={() => setCurrentView('calendar')}
          >
            📅 校曆表
          </button>
          <button 
            className={`nav-btn ${currentView === 'activities' ? 'active' : ''}`}
            onClick={() => setCurrentView('activities')}
          >
            📋 活動列表
          </button>
          <button 
            className={`nav-btn ${currentView === 'students' ? 'active' : ''}`}
            onClick={() => setCurrentView('students')}
          >
            👥 學生記錄
          </button>
        </div>
      </div>

      <div className="slp-content">
        <StatisticsPanel stats={stats} />

        {currentView === 'calendar' && (
          <CalendarView
            activities={getFilteredActivities()}
            selectedDate={selectedDate}
            filters={filters}
            onDateSelect={handleDateSelect}
            onActivitySelect={handleActivitySelect}
            onFilterChange={handleFilterChange}
          />
        )}

        {currentView === 'activities' && (
          <ActivityList
            activities={getFilteredActivities()}
            filters={filters}
            onActivitySelect={handleActivitySelect}
            onActivityDelete={handleActivityDelete}
            onFilterChange={handleFilterChange}
            onAddActivity={() => {
              setSelectedActivity(null);
              setShowActivityForm(true);
            }}
          />
        )}

        {currentView === 'students' && (
          <StudentRecords
            students={students}
            selectedStudent={selectedStudent}
            onStudentSelect={handleStudentSelect}
          />
        )}

        {showActivityForm && (
          <ActivityForm
            activity={selectedActivity}
            students={students}
            teachers={teachers}
            onSave={handleActivitySave}
            onCancel={() => {
              setShowActivityForm(false);
              setSelectedActivity(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SlpModule;
